import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  PlannerConfigEntityWithOptionalFields,
  PlannerConfigMarketDataEntity,
} from '@/services/configurator/PlannerConfigTypes';
import { PlannerFormItemMarketDataDialog } from './planner-form-market-data-dialog';
import { Switch } from '@/components/ui/switch';
import { listMarkets } from '@ingka-group-digital/pnv-shared';
import { useUser } from '@/components/providers/user-provider';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { useEffect } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hasPermission } from '@/utils/validation';
import { MARKET_ENABLE_TOGGLE_PERMISSIONS } from '@/config/feature-permissions';
import { useEnv } from '@/components/providers/env-provider';

type ValidErrorFields = {
  imageUrl: string;
  videoUrl: string;
  primaryCardCtaUrl: string;
  secondaryBannerCtaUrl: string;
  secondaryBannerImgUrl: string;
  secondaryCardCtaUrl: string;
};

export type PlannerFormItemMarketDataProps = {
  marketData: PlannerConfigMarketDataEntity[];
  onMarketDataChange: (marketData: PlannerConfigMarketDataEntity[]) => void;
  disabled?: boolean;
  disableIdFields?: boolean;
  form: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  field: ControllerRenderProps<
    PlannerConfigEntityWithOptionalFields,
    'marketData'
  >;
  dynamicMarketData?: PlannerConfigMarketDataEntity[] | null;
  initialState: PlannerConfigEntityWithOptionalFields;
};

export function PlannerFormItemMarketData(
  props: PlannerFormItemMarketDataProps,
) {
  const user = useUser();
  const { env } = useEnv();

  function handleCheckedChange(
    checked: boolean,
    market: string,
    marketData: PlannerConfigMarketDataEntity | undefined,
  ) {
    if (!marketData) {
      const createdMarketData = [...props.marketData];

      createdMarketData.push({
        includeDeeplinkParams: true,
        isActive: checked,
        locale: market,
        imageUrl: '',
        videoUrl: '',
        primaryCardCtaUrl: '',
        secondaryBannerCtaUrl: '',
        secondaryBannerImgUrl: '',
        secondaryCardCtaUrl: '',
        productsInclude: [],
        productsExclude: [],
        rangeInclude: [],
        rangeExclude: [],
      });

      props.onMarketDataChange(createdMarketData);
    } else {
      if (props?.dynamicMarketData) {
        const updatedMarketData = props.dynamicMarketData.map((marketData) => {
          if (market === marketData.locale) {
            return {
              ...marketData,
              isActive: checked,
              includeDeeplinkParams: checked,
            };
          }
          return marketData;
        });

        props.onMarketDataChange(updatedMarketData);
      }
    }
  }

  useEffect(() => {
    if (props?.marketData?.length) {
      props.form.trigger('marketData');
    }
  }, [props.marketData]);

  const hasMarketOverrides = (data: PlannerConfigMarketDataEntity) => {
    if (!data || !data.isActive) {
      return false;
    }

    const fieldsToCheck = [
      'productsInclude',
      'productsExclude',
      'rangeInclude',
      'rangeExclude',
      'imageUrl',
      'secondaryBannerImgUrl',
      'primaryCardCtaUrl',
      'secondaryBannerCtaUrl',
      'secondaryCardCtaUrl',
      'videoUrl',
    ];

    return fieldsToCheck.some((field) => {
      const value = data[field as keyof PlannerConfigMarketDataEntity];
      return Array.isArray(value) ? value.length > 0 : !!value;
    });
  };

  function resetFormErrors(marketIndex: number) {
    const currentValues = props.form.getValues(`marketData.${marketIndex}`);

    const errorKeys = Object.keys(
      props.form.formState.errors.marketData?.[marketIndex] || {},
    );

    const updatedValues = { ...currentValues };
    errorKeys.forEach((key) => {
      updatedValues[key as keyof ValidErrorFields] = '';
    });

    props.form.setValue(`marketData.${marketIndex}`, updatedValues);
    props.form.trigger();
  }

  function enableAllMarkets(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const updatedMarketData = [...(props.dynamicMarketData || [])];

    listMarkets().forEach((locale) => {
      const existingIndex = updatedMarketData.findIndex(
        (x) => x.locale === locale,
      );
      if (existingIndex !== -1) {
        // If market exists, set isActive to true
        updatedMarketData[existingIndex] = {
          ...updatedMarketData[existingIndex],
          isActive: true,
        };
      } else {
        // If market does not exist, add a new entry with isActive true
        updatedMarketData.push({
          includeDeeplinkParams: true,
          isActive: true,
          locale,
          imageUrl: '',
          videoUrl: '',
          primaryCardCtaUrl: '',
          secondaryBannerCtaUrl: '',
          secondaryBannerImgUrl: '',
          secondaryCardCtaUrl: '',
          productsInclude: [],
          productsExclude: [],
          rangeInclude: [],
          rangeExclude: [],
        });
      }
    });

    props.onMarketDataChange(updatedMarketData);
  }

  function resetMarketsToInitialState(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    props.onMarketDataChange(
      props.initialState.marketData as PlannerConfigMarketDataEntity[],
    );
  }

  function disableAllMarkets(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const updatedMarketData = [
      ...(props.dynamicMarketData as PlannerConfigMarketDataEntity[]),
    ];

    listMarkets().forEach((locale) => {
      const existingIndex = updatedMarketData.findIndex(
        (x) => x.locale === locale,
      );
      updatedMarketData[existingIndex] = {
        ...updatedMarketData[existingIndex],
        isActive: false,
      };
      return updatedMarketData;
    });
    props.onMarketDataChange(updatedMarketData);
  }

  const sortedLocales = listMarkets().sort((a, b) => {
    const countryA = a.split('-')[1];
    const countryB = b.split('-')[1];
    return countryA.localeCompare(countryB);
  });

  const canToggleMarketEnable = hasPermission(
    MARKET_ENABLE_TOGGLE_PERMISSIONS[env],
    user.role,
    user.editConfigEnabled,
  );
  const hasRestrictions =
    Object.keys(user.restrictions?.markets || {}).length > 0;
  const canToggle = canToggleMarketEnable && !hasRestrictions;
  const isDisabled = props.disabled || !canToggle;
  return (
    <FormItem className='col-span-2'>
      <div className='flex px-3 py-2 rounded-md border border-input flex-col'>
        <p className='text-sm font-medium text-muted-foreground mb-1'>
          Symbol explanation:
        </p>
        <div className='flex items-center'>
          <CircleCheck
            className='flex items-center justify-center fill-lime-500 text-white mr-1'
            size={24}
          />
          <p className='text-sm font-medium text-muted-foreground'>
            Marketdata has overrides and no errors |
          </p>
          <CircleAlert
            className='flex items-center justify-center fill-rose-500 text-white mr-1 ml-1'
            size={24}
          />
          <p className='text-sm font-medium text-muted-foreground'>
            Marketdata has overrides with errors
          </p>
        </div>
      </div>
      <div className='flex justify-between items-center'>
        <FormLabel>Market data</FormLabel>
        <div className='my-2'>
          <Button
            onClick={enableAllMarkets}
            className='mr-1'
            disabled={isDisabled}
          >
            Enable all markets
          </Button>
          <Button
            onClick={disableAllMarkets}
            className='mr-1'
            disabled={isDisabled}
          >
            Disable all markets
          </Button>
          <Button onClick={resetMarketsToInitialState} disabled={isDisabled}>
            Reset to initial
          </Button>
        </div>
      </div>
      <FormControl>
        <div className='flex flex-col gap-4 border-solid border-1 rounded p-4'>
          <ul className='list-none grid gap-2 grid-rows-[repeat(17,auto)] grid-flow-col'>
            {sortedLocales.map((market, index) => {
              const marketData = (
                props?.dynamicMarketData || props.marketData
              ).find((marketData) => marketData.locale === market);

              const [, country] = market.split('-');

              const restrictedToMarket =
                user.restrictions.markets &&
                !user.restrictions.markets?.includes(country);

              return (
                <li
                  key={index}
                  className='flex items-center gap-2 relative pl-4'
                >
                  <Switch
                    disabled={props.disabled || restrictedToMarket}
                    checked={marketData?.isActive ?? false}
                    onCheckedChange={(checked) =>
                      handleCheckedChange(checked, market, marketData)
                    }
                  />
                  <div className='flex items-center justify-around'>
                    <p className='min-w-[50px]'>{market}</p>
                    {marketData && (
                      <PlannerFormItemMarketDataDialog
                        form={props.form}
                        field={props.field}
                        currentMarket={market}
                        hasMarketOverrides={hasMarketOverrides}
                        marketData={props.dynamicMarketData}
                        onMarketDataChange={props.onMarketDataChange}
                        data={marketData}
                        disabled={props.disabled || restrictedToMarket}
                        disableIdFields={props.disableIdFields}
                        resetFormErrors={resetFormErrors}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </FormControl>
      <FormDescription>
        Add, edit, delete or activate/deactive market specific configuration.
      </FormDescription>
      <FormMessage />
    </FormItem>
  );
}
